import type { TOC } from '@ember/component/template-only';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import {
  PrismBadge,
  PrismButton,
  PrismComboSelectInput,
  PrismDrawer,
  PrismIcon,
  PrismIconFlag,
  PrismLoadingBar,
  PrismPerfectScrollbar,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, eq, not, or } from 'ember-truth-helpers';
import GeneralButtonsButtonExport from 'lumen/components/general/buttons/button-export';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import ParityOtaWithIcon from 'lumen/components/parity/ota-with-icon';
import ParityParityManagerDrawerSection from 'lumen/components/parity/parity-manager/drawer-section';
import ParityParityManagerInputFieldSearch from 'lumen/components/parity/parity-manager/input-field-search';
import ParityParityManagerMetricBlock from 'lumen/components/parity/parity-manager/metric-block';
import ParityParityManagerOtherInformation from 'lumen/components/parity/parity-manager/other-information';
import ParityParityManagerSidebarItem from 'lumen/components/parity/parity-manager/sidebar-item';
import ParityParityManagerTableIssues from 'lumen/components/parity/parity-manager/table-issues';
import type MarketParityParityManagerController from 'lumen/controllers/market/parity/parity-manager';
import can from 'lumen/helpers/can';
import isDrawerExpanded from 'lumen/helpers/is-drawer-expanded';

interface MarketParityParityManagerSignature {
  Args: {
    controller: MarketParityParityManagerController;
    model: unknown;
  };
}

<template>
  <GeneralPageHeader
    class="parity-manager"
    {{didInsert
      @controller.onIssueSummariesDataUpdate
      @controller.summariesData
    }}
    {{didUpdate
      @controller.onIssueSummariesDataUpdate
      @controller.summariesData
    }}
  >
    <div class="content-wrapper">
      <div class="parity-manager-datepicker-wrapper mr-200">
        {{! @glint-expect-error: Argument of type 'typeof GeneralDatepickersDatepickerWithSidebar' is not assignable to parameter of type 'DirectInvokable'. }}
        <GeneralDatepickersDatepickerWithSidebar
          @calendarFocusOnEnd={{true}}
          @maxDate={{@controller.datePickerMaxDate}}
          @maxRange="31 days"
          @minDate={{@controller.datePickerMinDate}}
          @range={{@controller.range}}
          @rangeChanged={{@controller.rangeChanged}}
          @sidebarLists={{@controller.datePickerRangeOptions}}
          @triggerLabel={{t "general.shopped_in"}}
        />
      </div>

      <PrismPerfectScrollbar
        @customContainerId="parity-manager-navbar"
        @suppressScrollY={{true}}
        id="pm-navbar-scrollbar-container"
      >
        <div id="parity-manager-navbar">
          <div class="ota-selections-container gap-200">
            <div class="select-wrapper">
              <PrismComboSelectInput
                @buildLabelWithCount={{fn
                  @controller.selectsBuildLabelWithCount
                  "parity_manager.channel.count"
                }}
                @isSearchable={{true}}
                @onChange={{fn
                  @controller.handleChangeForType
                  "channels"
                  @controller.possibleChannels.length
                }}
                @options={{@controller.possibleChannels}}
                @selectAllLabel={{t "parity_manager.channel.count.any"}}
                @value={{@controller.possibleChannelsValue}}
              />
            </div>

            <div class="select-wrapper">
              <PrismComboSelectInput
                @buildLabelWithCount={{fn
                  @controller.selectsBuildLabelWithCount
                  "parity_manager.assignee.count"
                }}
                @isSearchable={{true}}
                @onChange={{fn
                  @controller.handleChangeForType
                  "assignees"
                  @controller.possibleAssigneesWithDefault.length
                }}
                @options={{@controller.possibleAssigneesWithDefault}}
                @selectAllLabel={{t "parity_manager.assignee.count.any"}}
                @value={{@controller.possibleAssigneesValue}}
              />
            </div>

            <div class="select-wrapper">
              <PrismComboSelectInput
                @buildLabelWithCount={{fn
                  @controller.selectsBuildLabelWithCount
                  "parity_manager.status.count"
                }}
                @onChange={{fn
                  @controller.handleChangeForType
                  "statuses"
                  @controller.possibleStatusesWithDefault.length
                }}
                @options={{@controller.possibleStatusesWithDefault}}
                @selectAllLabel={{t "parity_manager.status.count.any"}}
                @value={{@controller.possibleStatusesValue}}
              />
            </div>

            <div class="select-wrapper">
              <PrismComboSelectInput
                @buildLabelWithCount={{fn
                  @controller.selectsBuildLabelWithCount
                  @controller.rootCauseOrWholesalerPrefix
                }}
                @isSearchable={{true}}
                @onChange={{fn
                  @controller.handleChangeForType
                  "rootcauses"
                  @controller.possibleRootcausesWithDefault.length
                }}
                @options={{@controller.possibleRootcausesWithDefault}}
                @selectAllLabel={{t
                  (concat @controller.rootCauseOrWholesalerPrefix ".any")
                }}
                @value={{@controller.possibleRootcausesValue}}
              />
            </div>

            <div class="select-wrapper">
              <PrismComboSelectInput
                @buildLabelWithCount={{fn
                  @controller.selectsBuildLabelWithCount
                  "parity_manager.escalation_level.count"
                }}
                @onChange={{fn
                  @controller.handleChangeForType
                  "escalationLevels"
                  @controller.possibleEscalationLevels.length
                }}
                @options={{@controller.possibleEscalationLevels}}
                @selectAllLabel={{t
                  "parity_manager.escalation_level.count.any"
                }}
                @value={{@controller.possibleEscalationLevelsValue}}
              />
            </div>

            <div class="select-wrapper">
              <PrismComboSelectInput
                @buildLabelWithCount={{fn
                  @controller.selectsBuildLabelWithCount
                  "parity_manager.entry_mode.count"
                }}
                @onChange={{fn
                  @controller.handleChangeForType
                  "entryModes"
                  @controller.possibleEntryModes.length
                }}
                @options={{@controller.possibleEntryModes}}
                @selectAllLabel={{t "parity_manager.entry_mode.count.any"}}
                @value={{@controller.possibleEntryModesValue}}
              />
            </div>

            <div class="parity-manager-search-select mr-100 flex items-center">
              {{#if @controller.isSearchActive}}
                <ParityParityManagerInputFieldSearch
                  @searchValue={{@controller.searchValue}}
                  @toggleSearch={{@controller.toggleSearch}}
                  @updateSearchValue={{@controller.updateSearchValue}}
                />
              {{else}}
                {{#if @controller.searchValue}}
                  <PrismButton
                    @buttonType="secondary"
                    @iconAfter="cross"
                    @label={{@controller.searchValue}}
                    @onClick={{@controller.resetSearchValue}}
                    class="current-search-value truncate"
                    title={{@controller.searchValue}}
                  />
                {{/if}}
                <PrismButton
                  @buttonType="ghost"
                  @icon="search"
                  @onClick={{fn @controller.toggleSearch true}}
                  @size="small"
                  class="mr-200"
                />
              {{/if}}
            </div>

          </div>
        </div>
      </PrismPerfectScrollbar>

      <div class="flex flex-1 items-center justify-end">
        <GeneralButtonsButtonExport
          @exportAction={{@controller.handleExport}}
        />
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageMain>
    <div class="parity-manager content-wrapper main-content">
      {{#if @controller.isDataRequestRunning}}
        <PrismLoadingBar />
      {{/if}}

      <div class="parity-manager__navigation">
        <h3 class="major-channels" slot="header-title">
          {{t "parity_analytics.contract"}}
        </h3>
        <h3 class="other-channels" slot="header-title">
          {{t "parity_analytics.non-contract"}}
        </h3>

        {{#each @controller.metrics as |metric|}}
          <div class="metric-cell">
            <ParityParityManagerMetricBlock
              @metric={{metric}}
              @setSelectedMetric={{@controller.setSelectedMetric}}
            />
          </div>
        {{/each}}
      </div>

      <div class="parity-manager__issuesummaries mt-800">
        <div class="flex items-center justify-between">
          <div
            class="text-neutral-emphasis mr-200 flex items-center justify-start gap-200"
          >
            <h2>{{@controller.selectedMetricObject.tableTitle}}</h2>
            {{#if
              (and
                (can "market.paritymanager.manualissues.add")
                @controller.isTodayInRange
              )
            }}
              <PrismButton
                @buttonType="secondary"
                @iconBefore="plus"
                @label={{t "parity_manager.add_issue"}}
                @onClick={{@controller.addIssue}}
                @size="small"
              />
            {{/if}}
          </div>
        </div>

        <div class="card-tile mt-400 overflow-hidden">
          <div class="issuesummary">
            <div class="issuesummary__sidebar">
              <div
                class="issuesummary__sidebar__header flex items-center justify-start"
              >
                {{#if @controller.isDataRequestRunning}}
                  <p class="loading-pulse--col-6"></p>
                {{else if @controller.selectedMetricObject}}
                  <h3>{{@controller.selectedMetricObject.sidebarTitle}}</h3>
                  <div class="ml-100">
                    <PrismBadge @label={{@controller.issueSummaries.length}} />
                  </div>
                {{/if}}
              </div>

              <div class="issuesummary__sidebar__items">
                {{#unless @controller.isDataRequestRunning}}
                  <div class="issuesummary__sidebar__container">
                    {{#each @controller.issueSummaries as |issueSummary|}}
                      <ParityParityManagerSidebarItem
                        @isSelected={{eq
                          issueSummary.id
                          @controller.selectedIssueSummaryId
                        }}
                        @issueSummary={{issueSummary}}
                        @onIssueSummaryClick={{@controller.onIssueSummaryClick}}
                      />
                    {{/each}}
                  </div>
                {{/unless}}
              </div>
            </div>

            <div class="issuesummary__table">
              <div
                class="issuesummary__table__header flex items-center justify-start p-400"
              >
                {{#if
                  (or
                    @controller.isDataRequestRunning
                    @controller.fetchIssuesForIssueSummaryTask.isRunning
                  )
                }}
                  <p class="loading-pulse w-1/3"></p>
                {{else if @controller.selectedIssueSummary}}
                  <h3 class="truncate">
                    {{@controller.selectedIssueSummary.formattedName}}
                  </h3>
                  <div class="ml-100">
                    <PrismBadge
                      @label={{@controller.selectedIssueSummary.amountIssues}}
                      @type="info"
                    />
                  </div>
                {{/if}}
              </div>
              {{#if
                (and
                  (not @controller.isDataRequestRunning)
                  (not @controller.fetchIssuesForIssueSummaryTask.isRunning)
                  @controller.issuesForExpandedIssueSummary
                  @controller.selectedIssueSummary
                )
              }}
                <div class="issuesummary__table__body">
                  <ParityParityManagerTableIssues
                    @issues={{@controller.issuesForExpandedIssueSummary}}
                    @onRowClick={{@controller.onRowClick}}
                    @rootCauses={{@controller.possibleRootcausesWithDefault}}
                    @selectedMetric={{@controller.selectedMetric}}
                    @statuses={{@controller.possibleStatusesWithDefault}}
                  />
                </div>
              {{/if}}
            </div>
          </div>
        </div>
      </div>
    </div>
  </GeneralPageMain>

  <PrismDrawer
    @contentPadding="none"
    @isOpen={{isDrawerExpanded @controller.PM_DRAWER_ID}}
    @positionTop={{120}}
  >
    <:content>
      <div class="fixed-drawer-header mb-400 flex items-center justify-between">
        <h3>
          {{or
            @controller.selectedIssue.customIssueTitle
            @controller.selectedMetricObject.drawerTitle
          }}
        </h3>

        <button
          class="button--unstyled"
          type="button"
          {{on "click" @controller.collapseDrawer}}
        >
          <PrismIcon @name="cross" @size="small" />
        </button>
      </div>

      <div class="drawer-content">
        {{#if @controller.isTestBookingView}}
          <ParityParityManagerDrawerSection
            @subtitle={{t "general.booking_details.info"}}
            @title={{t "general.booking_details"}}
          >
            <div class="drawer-section__grid">
              <p class="text-300--regular truncate">
                {{t "settings.account.first_name"}}
              </p>
              <p class="text-300--regular truncate">
                {{@controller.selectedIssue.guestFirstName}}
              </p>
              <p class="text-300--regular truncate">
                {{t "settings.account.last_name"}}
              </p>
              <p class="text-300--regular truncate">
                {{@controller.selectedIssue.guestLastName}}
              </p>
              <p class="text-300--regular truncate">
                {{t "general.booking_id"}}
              </p>
              <p class="text-300--regular truncate">
                {{@controller.selectedIssue.bookingId}}
              </p>
            </div>
          </ParityParityManagerDrawerSection>
        {{/if}}

        <ParityParityManagerDrawerSection
          @subtitle={{t "general.hotel_details.info"}}
          @title={{t "general.hotel_details"}}
        >
          <div class="drawer-section__grid">
            <p class="text-300--regular truncate">
              {{t "parity_analytics.brand_code"}}
            </p>
            <p class="text-300--regular truncate">
              {{@controller.selectedIssue.brandCode}}
            </p>
            <p class="text-300--regular truncate">
              {{t "general.hotel.plural" count=1}}
            </p>
            <p
              class="paragraph-300--regular line-clamp-2"
              title={{@controller.selectedIssue.hotelName}}
            >
              {{@controller.selectedIssue.hotelName}}
            </p>
            {{#if @controller.isMajorOtaView}}
              <p class="text-300--regular truncate">
                {{t "general.platform.plural" count=1}}
              </p>
              <p class="text-300--regular truncate">
                {{@controller.selectedIssue.formattedBasePlatform}}
              </p>
            {{/if}}
            {{#if @controller.selectedIssue.baseRoomName}}
              <p class="text-300--regular truncate">
                {{t "general.room_name"}}
              </p>
              <p
                class="text-300--regular truncate"
                title={{@controller.selectedIssue.baseRoomName}}
              >
                {{@controller.selectedIssue.baseRoomName}}
              </p>
            {{/if}}
            <p class="text-300--regular truncate">
              {{t "rates.rate"}}
            </p>
            <div class="flex items-center justify-start">
              <a
                class="prism-link prism-link--medium overflow-hidden"
                href={{@controller.selectedIssue.baseRedirect}}
              >
                <span class="truncate">
                  {{@controller.selectedIssue.formattedBaseRate}}
                </span>
                {{#if @controller.selectedIssue.baseCancellation}}
                  <PrismIcon
                    @color="inherit"
                    @name="rates-best-flex"
                    @size="small"
                  />
                {{/if}}
              </a>
            </div>

            {{#if
              (and
                @controller.isMajorOtaView
                @controller.selectedIssue.subscriptionTaxInclText.length
              )
            }}
              <p class="text-300--regular truncate">
                {{t "general.taxes"}}
              </p>
              <p
                class="text-300--regular truncate"
                title={{@controller.selectedIssue.subscriptionTaxInclText}}
              >
                {{@controller.selectedIssue.subscriptionTaxInclText}}
              </p>
              {{#unless @controller.selectedIssue.isNormalized}}
                <p class="text-300--regular truncate">
                  {{t "general.shopped_rate"}}
                </p>
                <p class="text-300--regular truncate">
                  {{@controller.selectedIssue.baseFormattedShoppedRate}}
                </p>
                <p class="text-300--regular truncate">
                  {{t "general.shopped_taxes"}}
                </p>
                <p
                  class="text-300--regular truncate"
                  title={{@controller.selectedIssue.baseTaxInclText}}
                >
                  {{@controller.selectedIssue.baseTaxInclText}}
                </p>
              {{/unless}}
            {{/if}}
          </div>
        </ParityParityManagerDrawerSection>

        {{#if @controller.isBaitAndSwitchView}}
          <ParityParityManagerDrawerSection
            @subtitle={{t "general.meta_details.info"}}
            @title={{t "general.meta_details"}}
          >
            <div class="drawer-section__grid">
              <p class="text-300--regular truncate">{{t "general.meta"}}</p>
              <p class="text-300--regular truncate">
                {{@controller.selectedIssue.metaId}}
              </p>
              {{#if @controller.selectedIssue.metaRoomName}}
                <p class="text-300--regular truncate">
                  {{t "general.room_name"}}
                </p>
                <p
                  class="text-300--regular truncate"
                  title={{@controller.selectedIssue.metaRoomName}}
                >
                  {{@controller.selectedIssue.metaRoomName}}
                </p>
              {{/if}}
              <p class="text-300--regular truncate">{{t "rates.rate"}}</p>
              <div class="flex items-center justify-start">
                <a
                  class="prism-link prism-link--medium overflow-hidden"
                  href={{@controller.selectedIssue.metasearchRedirect}}
                >
                  <span class="truncate">
                    {{@controller.selectedIssue.formattedMetaRate}}
                  </span>
                  {{#if @controller.selectedIssue.metaCancellation}}
                    <PrismIcon
                      @color="inherit"
                      @name="rates-best-flex"
                      @size="small"
                    />
                  {{/if}}
                </a>
              </div>
            </div>
          </ParityParityManagerDrawerSection>
        {{/if}}

        {{#if
          (and
            @controller.isMajorOtaView
            @controller.selectedIssueSummary.escalationinfo.hasEscalationLevelWarningOrAbove
          )
        }}
          <ParityParityManagerDrawerSection
            @subtitle={{t "general.escalation_details.info"}}
            @title={{t "general.escalation_details"}}
          >
            <div class="drawer-section__grid">
              <p
                class="text-300--regular truncate"
                title={{t "parity_manager.tooltip.days_with_confirmed_issues"}}
              >
                {{t "parity_manager.tooltip.days_with_confirmed_issues"}}
              </p>
              <p class="text-300--regular truncate">
                {{@controller.selectedIssueSummary.escalationinfo.numDaysWithConfirmed}}
              </p>
              {{#if
                @controller.selectedIssueSummary.escalationinfo.formattedFirstConfirmedDate
              }}
                <p class="text-300--regular truncate">
                  {{t "parity_manager.tooltip.first_confirmed_date"}}
                </p>
                <p class="text-300--regular truncate">
                  {{@controller.selectedIssueSummary.escalationinfo.formattedFirstConfirmedDate}}
                </p>
              {{/if}}
              {{#if
                @controller.selectedIssueSummary.escalationinfo.formattedFirstLevelOneIssueDate
              }}
                <p class="text-300--regular truncate">
                  {{t "parity_manager.tooltip.first_issue_date"}}
                </p>
                <p class="text-300--regular">
                  {{@controller.selectedIssueSummary.escalationinfo.formattedFirstLevelOneIssueDate}}
                </p>
              {{/if}}
              {{#if
                @controller.selectedIssueSummary.escalationinfo.formattedLastConfirmedDate
              }}
                <p class="text-300--regular truncate">
                  {{t "parity_manager.tooltip.last_confirmed_date"}}
                </p>
                <p class="text-300--regular truncate">
                  {{@controller.selectedIssueSummary.escalationinfo.formattedLastConfirmedDate}}
                </p>
              {{/if}}
              <p class="text-300--regular truncate">
                {{t "parity_manager.tooltip.issues_confirmed"}}
              </p>
              <p class="text-300--regular truncate">
                {{@controller.selectedIssueSummary.escalationinfo.numConfirmed}}
              </p>
              <p class="text-300--regular truncate">
                {{t "parity_manager.tooltip.issues_waived"}}
              </p>
              <p class="text-300--regular truncate">
                {{@controller.selectedIssueSummary.escalationinfo.numWaived}}
              </p>
            </div>
          </ParityParityManagerDrawerSection>
        {{/if}}

        <ParityParityManagerDrawerSection
          @subtitle={{t "general.channel_details.info"}}
          @title={{t "general.channel_details"}}
        >
          <div class="drawer-section__grid">
            <p class="text-300--regular truncate">{{t "parity.channel"}}</p>
            {{#if @controller.selectedIssue.channelId}}
              {{#if @controller.selectedIssue.isMajorOTAIssue}}
                {{! @glint-expect-error: Property 'size' is missing in type '{ otaId: string; }' but required in type '{ otaId: string; size: "small" | "medium"; }'. }}
                <ParityOtaWithIcon
                  @otaId={{@controller.selectedIssue.channelId}}
                />
              {{else}}
                <p class="text-300--regular truncate">
                  {{@controller.selectedIssue.formattedChannel}}
                </p>
              {{/if}}
            {{/if}}
            {{#if @controller.selectedIssue.hasExtranetUrl}}
              <p class="text-300--regular truncate"></p>
              <a
                class="prism-link drawer-section__grid__link flex flex-row items-center gap-100"
                href={{@controller.selectedIssue.extranetUrl}}
                rel="noopener noreferrer"
                target="_blank"
              >
                <p class="text-300--regular truncate">
                  {{t "parity.extranet_login"}}
                </p>
                <PrismIcon @color="inherit" @name="link-out" @size="small" />
              </a>
            {{/if}}
            {{#if (@controller.selectedIssue.hasChannelPlatform)}}
              <p class="text-300--regular truncate">
                {{t "general.platform.plural" count=1}}
              </p>
              <p class="text-300--regular truncate">
                {{@controller.selectedIssue.formattedChannelPlatform}}
              </p>
            {{/if}}
            {{#if @controller.selectedIssue.channelRoomName}}
              <p class="text-300--regular truncate">
                {{t "general.room_name"}}
              </p>
              <p
                class="text-300--regular truncate"
                title={{@controller.selectedIssue.channelRoomName}}
              >
                {{@controller.selectedIssue.channelRoomName}}
              </p>
            {{/if}}
            <p class="text-300--regular truncate">{{t "rates.rate"}}</p>
            <div class="flex items-center justify-start">
              <a
                class="prism-link prism-link--medium overflow-hidden"
                href={{@controller.selectedIssue.channelRedirect}}
              >
                <span class="truncate">
                  {{@controller.selectedIssue.formattedChannelRate}}
                </span>
                {{#if @controller.selectedIssue.channelCancellation}}
                  <PrismIcon
                    @color="inherit"
                    @name="rates-best-flex"
                    @size="small"
                  />
                {{/if}}
              </a>
            </div>
            {{#if @controller.selectedIssue.hasLiveViewUrl}}
              <p class="text-300--regular truncate"></p>
              <div class="flex items-center justify-start">
                <a
                  class="prism-link drawer-section__grid__link flex flex-row items-center gap-100"
                  href={{@controller.selectedIssue.liveUrl}}
                  {{on
                    "click"
                    (fn @controller.trackSidePanelAction "Live View Opened")
                  }}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <p class="text-300--regular truncate">
                    {{t "parity_manager.visit_live_view"}}
                  </p>
                  <PrismIcon
                    @color="inherit"
                    @name="visibility-on"
                    @size="small"
                  />
                </a>
              </div>
            {{/if}}
            {{#unless @controller.isBaitAndSwitchView}}
              <p class="text-300--regular truncate">
                {{t "general.difference"}}
              </p>
              <p class="text-300--regular text-critical-default truncate">
                {{@controller.selectedIssue.formattedAbsoluteDifferenceWithBaserate}}
              </p>
            {{/unless}}

            {{#if
              (and
                @controller.isMajorOtaView
                @controller.selectedIssue.subscriptionTaxInclText.length
              )
            }}
              <p class="text-300--regular truncate">{{t "general.taxes"}}</p>
              <p
                class="text-300--regular truncate"
                title={{@controller.selectedIssue.subscriptionTaxInclText}}
              >
                {{@controller.selectedIssue.subscriptionTaxInclText}}
              </p>
              {{#unless @controller.selectedIssue.isNormalized}}
                <p class="text-300--regular truncate">
                  {{t "general.shopped_rate"}}
                </p>
                <p class="text-300--regular truncate">
                  {{@controller.selectedIssue.channelFormattedShoppedRate}}
                </p>
                <p class="text-300--regular truncate">
                  {{t "general.shopped_taxes"}}
                </p>
                <p
                  class="text-300--regular truncate"
                  title={{@controller.selectedIssue.channelTaxInclText}}
                >
                  {{@controller.selectedIssue.channelTaxInclText}}
                </p>
              {{/unless}}
            {{/if}}
          </div>
        </ParityParityManagerDrawerSection>

        <ParityParityManagerDrawerSection
          @subtitle={{t "general.shared_details.info"}}
          @title={{t "general.shared_details"}}
        >
          <div class="drawer-section__grid">
            <p class="text-300--regular truncate">
              {{t "general.shopped_date"}}
            </p>
            <p class="text-300--regular truncate">
              {{@controller.selectedIssue.formattedExtractDate}}
            </p>
            <p class="text-300--regular truncate">
              {{t "general.arrival_date"}}
            </p>
            <p class="text-300--regular truncate">
              {{@controller.selectedIssue.formattedArrivalDate}}
            </p>
            <p class="text-300--regular truncate">{{t "rates.los"}}</p>
            <p class="text-300--regular truncate">
              {{@controller.selectedIssue.los}}
            </p>
            <p class="text-300--regular truncate">
              {{t "general.pos_short"}}
            </p>
            {{#if @controller.selectedIssue.channelPos}}
              <PrismIconFlag @code={{@controller.selectedIssue.channelPos}} />
            {{else}}
              <p class="text-300--regular truncate">
                {{@controller.selectedIssue.formattedChannelPos}}
              </p>
            {{/if}}
          </div>
        </ParityParityManagerDrawerSection>

        <ParityParityManagerDrawerSection
          @disableBorder={{true}}
          @title={{t "general.other_information"}}
        >
          <ParityParityManagerOtherInformation
            @filters={{@controller.filters}}
            @selectedIssue={{@controller.selectedIssue}}
            @selectedMetric={{@controller.selectedMetric}}
            @updateEscalationLevelForSubscription={{@controller.updateEscalationLevelForSubscription}}
          />
        </ParityParityManagerDrawerSection>
      </div>

      <div class="fixed-drawer-action">
        <PrismButton
          @iconBefore={{@controller.primaryActionIcon}}
          @label={{@controller.primaryActionName}}
          @onClick={{@controller.primaryAction}}
        />
      </div>
    </:content>
  </PrismDrawer>

  {{outlet}}
</template> satisfies TOC<MarketParityParityManagerSignature>;
